/**
 * FilterChips — Mobbin/Deel-style filter chip strip (TASK 1-5).
 *
 * Each chip shows "Label · N" when N options are selected. Clicking the
 * chip opens an anchored popover with checkboxes (multi) or radio buttons
 * (single). A "Clear all" link appears when any filter is active.
 *
 * Keyboard: Tab between chips, Enter/Space to open/close, Arrow keys
 * navigate options inside the popover, Escape closes.
 *
 * Visual reference:
 *   C:/PROJECT_GGJ_B2C/MobbinUI/UIPattern B2C/Table&Filter/Deel web Jan 2026 63.png
 */

import { Check, ChevronDown, X } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import type { FilterChipsProps, FilterDef } from './types';

// ─────────────────────────────────────────────────────────────────────────
// FilterChips (strip)
// ─────────────────────────────────────────────────────────────────────────

const FilterChips: React.FC<FilterChipsProps> = ({
  filters,
  activeValues,
  onChange,
  onClearAll,
}) => {
  const hasAny = Object.values(activeValues).some((v) => v.length > 0);

  return (
    <div
      role="group"
      aria-label="Filter chips"
      className="flex flex-wrap items-center gap-1.5"
    >
      {filters.map((f) => (
        <FilterChip
          key={f.id}
          filter={f}
          selected={activeValues[f.id] ?? []}
          onChange={(vals) => onChange(f.id, vals)}
        />
      ))}

      {hasAny && onClearAll && (
        <button
          type="button"
          onClick={onClearAll}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] font-medium rounded-full transition-colors"
          style={{ color: 'var(--brand-purple, #6C47FF)' }}
        >
          <X className="w-3 h-3" />
          Clear all
        </button>
      )}
    </div>
  );
};

export default FilterChips;

// ─────────────────────────────────────────────────────────────────────────
// Single chip + popover
// ─────────────────────────────────────────────────────────────────────────

interface FilterChipProps {
  filter:   FilterDef;
  selected: string[];
  onChange: (values: string[]) => void;
}

function FilterChip({ filter, selected, onChange }: FilterChipProps) {
  const [open, setOpen]   = useState(false);
  const chipRef           = useRef<HTMLButtonElement | null>(null);
  const popoverRef        = useRef<HTMLDivElement | null>(null);
  const [pos, setPos]     = useState({ top: 0, left: 0 });
  const popoverId         = useId();
  const multi             = filter.multi !== false; // default multi
  const count             = selected.length;
  const isActive          = count > 0;

  // Position popover below the chip.
  const recalcPos = useCallback(() => {
    if (!chipRef.current) return;
    const r = chipRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6 + window.scrollY, left: r.left + window.scrollX });
  }, []);

  const handleOpen = () => {
    recalcPos();
    setOpen(true);
  };

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;

    const down = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); chipRef.current?.focus(); }
    };
    const click = (e: MouseEvent) => {
      if (
        chipRef.current && !chipRef.current.contains(e.target as Node) &&
        popoverRef.current && !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const scroll = () => recalcPos();

    document.addEventListener('keydown', down);
    document.addEventListener('mousedown', click);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', scroll);

    return () => {
      document.removeEventListener('keydown', down);
      document.removeEventListener('mousedown', click);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', scroll);
    };
  }, [open, recalcPos]);

  const toggle = (value: string) => {
    if (multi) {
      const next = selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value];
      onChange(next);
    } else {
      onChange(selected[0] === value ? [] : [value]);
      setOpen(false);
    }
  };

  const chipClass = isActive ? 'chip-filter-active' : 'chip-filter';

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        className={chipClass}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => (open ? setOpen(false) : handleOpen())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); handleOpen(); }
        }}
      >
        <span>{filter.label}</span>
        {count > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[11px] font-bold px-1"
            style={{
              background: isActive ? 'var(--brand-purple, #6C47FF)' : 'var(--vscode-border-secondary)',
              color: isActive ? '#fff' : 'var(--vscode-text-primary)',
            }}
          >
            {count}
          </span>
        )}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && createPortal(
        <div
          ref={popoverRef}
          id={popoverId}
          role="listbox"
          aria-label={filter.label}
          aria-multiselectable={multi}
          className="fixed z-50 min-w-[220px] max-w-[320px] rounded-xl shadow-xl overflow-hidden"
          style={{
            top:         pos.top,
            left:        pos.left,
            background:  'var(--surface-card-elevated, var(--vscode-bg-elevated))',
            border:      '1px solid var(--vscode-border-primary)',
            boxShadow:   '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-3 py-2.5 border-b text-[12px] font-semibold"
            style={{ borderColor: 'var(--vscode-border-secondary)', color: 'var(--vscode-text-secondary)' }}
          >
            <span>{filter.label}</span>
            {multi && count > 0 && (
              <button
                type="button"
                className="text-[11px] font-medium"
                style={{ color: 'var(--brand-purple, #6C47FF)' }}
                onClick={() => onChange([])}
              >
                Clear
              </button>
            )}
          </div>

          {/* Options */}
          <ul className="max-h-[260px] overflow-y-auto py-1">
            {filter.options.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={checked}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-left transition-colors"
                    style={{
                      background: checked
                        ? 'var(--brand-purple-soft, rgba(108,71,255,0.08))'
                        : 'transparent',
                      color: 'var(--vscode-text-primary)',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = checked ? 'var(--brand-purple-soft,rgba(108,71,255,0.10))' : 'var(--vscode-bg-hover,rgba(0,0,0,0.05))'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = checked ? 'var(--brand-purple-soft,rgba(108,71,255,0.08))' : 'transparent'; }}
                    onClick={() => toggle(opt.value)}
                  >
                    {multi ? (
                      <span
                        className="flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center"
                        style={{
                          background:   checked ? 'var(--brand-purple, #6C47FF)' : 'transparent',
                          borderColor:  checked ? 'var(--brand-purple, #6C47FF)' : 'var(--vscode-border-primary)',
                        }}
                      >
                        {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                      </span>
                    ) : (
                      <span
                        className="flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center"
                        style={{
                          borderColor: checked ? 'var(--brand-purple, #6C47FF)' : 'var(--vscode-border-primary)',
                        }}
                      >
                        {checked && (
                          <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ background: 'var(--brand-purple, #6C47FF)' }}
                          />
                        )}
                      </span>
                    )}
                    <span>{opt.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Footer: multi only — show Apply count */}
          {multi && count > 0 && (
            <div
              className="px-3 py-2 border-t text-right"
              style={{ borderColor: 'var(--vscode-border-secondary)' }}
            >
              <button
                type="button"
                className="text-[12px] font-semibold px-3 py-1 rounded-lg"
                style={{
                  background: 'var(--brand-purple, #6C47FF)',
                  color:      'var(--brand-purple-on, #FFFFFF)',
                }}
                onClick={() => setOpen(false)}
              >
                Apply ({count})
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
